/** cause: 마지막 시도의 원본 에러 */
type FailedDelete<T> = { id: T; status?: number; cause?: unknown };

export type ChunkedDeleteResult<T> = {
  successCount: number;
  failed: FailedDelete<T>[];
};

const attemptDelete = async <T>(id: T, deleteOne: (id: T) => Promise<unknown>): Promise<FailedDelete<T> | undefined> => {
  try {
    await deleteOne(id);

    return undefined;
  } catch (error) {
    return { id, status: (error as { response?: { status?: number } })?.response?.status, cause: error };
  }
};

const splitIntoChunks = <T>(ids: T[], size: number): T[][] => {
  const chunks: T[][] = [];

  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }

  return chunks;
};

const deleteAll = async <T>(ids: T[], deleteOne: (id: T) => Promise<unknown>, chunkSize: number): Promise<FailedDelete<T>[]> => {
  const failed: FailedDelete<T>[] = [];

  // 청크마다 기다린다. 한꺼번에 보내면 서버에 한 번에 몰린다
  for (const group of splitIntoChunks(ids, chunkSize)) {
    const results = await Promise.all(group.map((id) => attemptDelete(id, deleteOne)));

    results.forEach((result) => {
      if (result) failed.push(result);
    });
  }

  return failed;
};

/**
 * 여러 건을 청크 단위로 순차 삭제하고, 실패분은 한 번만 다시 시도한다
 */
export const deleteInChunks = async <T>(ids: T[], deleteOne: (id: T) => Promise<unknown>, chunkSize = 20): Promise<ChunkedDeleteResult<T>> => {
  const failedOnce = await deleteAll(ids, deleteOne, chunkSize);
  const failed =
    failedOnce.length > 0
      ? await deleteAll(
          failedOnce.map((item) => item.id),
          deleteOne,
          chunkSize,
        )
      : [];

  return { successCount: ids.length - failed.length, failed };
};
